/** What POST /manpower/schedule/import hands back. */
export type BulkImportResult = {
  imported?: number;
  skipped?: number;
  matchedByName?: number;
  autoCreated?: number;
  /** Rows the caller sent that did NOT land. A strict subset of `skipped`. */
  droppedRows?: number;
  warnings?: string[];
};

/**
 * Should the import modal close itself once the import succeeds? (#478)
 *
 * It used to close unconditionally, on the same tick as the success. That made
 * the modal's own success summary unreachable — the component unmounted before
 * it could paint — and the `warnings` array was never rendered at all, so every
 * warning the API has ever produced went straight in the bin.
 *
 * That was survivable while warnings only ever said things like "matched by
 * name". It stopped being survivable when the server began reporting **rows it
 * did not import** because their job was deleted mid-run: a partial result,
 * presented to the administrator as a clean success, with no way to find out
 * which rows are missing. The same silent-success defect #478 is about, one
 * layer up from the database.
 *
 * ⚠️ THE RULE KEYS ON `droppedRows` AND NOTHING ELSE. The obvious two signals
 * are both wrong, and each one fires on a perfectly ordinary import:
 *
 *   * `warnings` — the API calls `pushWarn` every single time it auto-creates a
 *     placeholder project, which is routine and in which the data DID land. A
 *     blanket "any warning holds the screen" rule therefore held it open on most
 *     real imports. (This is exactly what the first version of this function
 *     did, and the test that was supposed to catch it asserted on
 *     `{ autoCreated: 3, warnings: [] }` — a combination the server cannot
 *     produce, so it passed while the real behavior was the opposite.)
 *   * `skipped` — also counts rows with zero crew, which are routine and where
 *     nothing was meant to land in the first place.
 *
 * A panel that appears on every import is one people learn to click through,
 * including the time it says their data is missing. So the screen is held open
 * only when rows the person actually sent failed to land.
 *
 * Extracted from the component because apps/web renders specs to static markup
 * with no DOM, and a rule about when a modal disappears is worth pinning.
 */
export function shouldCloseAfterImport(result: BulkImportResult | null | undefined): boolean {
  if (!result) return true;
  return (result.droppedRows ?? 0) === 0;
}
